import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyGetJobMaterialQuery } from '../job/jobEndpoints';
import { problemCode } from '../api/problemCode';
import { Icon } from '../common/Icon';

/** Props for `MaterialPageLink`. */
export interface MaterialPageLinkProps {
  jobId?: string;
  page: number;
}

/**
 * Cites the PDF page a draft was taken from: "PDF p. N" (QZ-D72). When the task has a
 * `jobId`, also shows an Open link, in `OpenMaterial`. Without one, the RTK hook never runs
 * and the label renders without a store `Provider` (PDG-D11).
 */
export const MaterialPageLink = ({ jobId, page }: MaterialPageLinkProps) => {
  const { t } = useTranslation();

  return (
    <>
      <span>{t('task.source.pdfPage', { page })}</span>
      {jobId && (
        <>
          {' · '}
          <OpenMaterial jobId={jobId} page={page} />
        </>
      )}
    </>
  );
};

/** The states `OpenMaterial`'s link can be in. */
type OpenState = 'idle' | 'loading' | 'expired' | 'failed';

/** Props for `OpenMaterial`. */
interface OpenMaterialProps {
  jobId: string;
  page: number;
}

/**
 * Fetches a short-lived GET URL for the job's material on click and opens it at `page` in a
 * new tab (QZ-D71, QZ-D72). Every click mints a fresh URL; nothing is cached.
 *
 * A browser only allows `window.open` within a click's own user activation, and an `await`
 * before it can lose that activation, so the blank tab is opened synchronously, before the
 * fetch even starts (PDG-D11).
 */
const OpenMaterial = ({ jobId, page }: OpenMaterialProps) => {
  const { t } = useTranslation();
  const [trigger] = useLazyGetJobMaterialQuery();
  const [state, setState] = useState<OpenState>('idle');

  const handleClick = async () => {
    setState('loading');
    // Not 'noopener': that makes `window.open` return null and the tab cannot be steered.
    // `tab.opener = null` gives the same isolation while keeping the reference.
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    try {
      const { url } = await trigger(jobId).unwrap();
      const target = `${url}#page=${page}`;
      if (tab) {
        tab.location.replace(target);
      } else {
        // The blank tab was popup-blocked. This runs after the fetch, so it is allowed only
        // while the click's activation lasts; a strict browser may block it too.
        window.open(target, '_blank', 'noopener');
      }
      setState('idle');
    } catch (error) {
      tab?.close();
      setState(problemCode(error) === 'MaterialExpired' ? 'expired' : 'failed');
    }
  };

  if (state === 'expired') {
    return <span className="qz-muted">{t('task.source.materialExpired')}</span>;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 qz-small"
        disabled={state === 'loading'}
        onClick={handleClick}
      >
        {t('task.source.open')} <Icon name="arrow-up-right-from-square" />
      </button>
      {state === 'failed' && <span className="text-danger"> {t('error.unknown')}</span>}
    </>
  );
};

export default MaterialPageLink;
